import React, { useCallback, useEffect, useRef } from 'react';
import { useEventListener } from 'usehooks-ts';

/**
 * The state of a key press event.
 */
export interface KeyPressState {
  /** The key that was pressed. */
  key: string;
  /** Whether the key is currently down. */
  down: boolean;
  /** Whether the alt key was pressed. */
  altKey: boolean;
  /** Whether the ctrl key was pressed. */
  ctrlKey: boolean;
  /** Whether the meta key was pressed. */
  metaKey: boolean;
  /** Whether the shift key was pressed. */
  shiftKey: boolean;
  /** The original event. */
  event?: KeyboardEvent;
}

interface UseKeyPressConfig {
  onKey: (state: KeyPressState) => void;
}

/**
 * A hook that listens for keyboard events and calls a handler with the state.
 */
export const useKeyPress = ({ onKey }: UseKeyPressConfig) => {
  const savedHandler = useRef(onKey);

  useEffect(() => {
    savedHandler.current = onKey;
  }, [onKey]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const state: KeyPressState = {
      key: event.key,
      down: true,
      altKey: event.altKey,
      ctrlKey: event.ctrlKey,
      metaKey: event.metaKey,
      shiftKey: event.shiftKey,
      event,
    };
    savedHandler.current(state);
  }, []);

  const handleKeyUp = useCallback((event: KeyboardEvent) => {
    const state: KeyPressState = {
      key: event.key,
      down: false,
      altKey: event.altKey,
      ctrlKey: event.ctrlKey,
      metaKey: event.metaKey,
      shiftKey: event.shiftKey,
      event,
    };
    savedHandler.current(state);
  }, []);

  useEventListener('keydown', handleKeyDown);
  useEventListener('keyup', handleKeyUp);

  // Return a function that returns the event handlers
  // This maintains compatibility with the original API
  return function bindKeyPress() {
    return {
      onKeyDown: (event: React.KeyboardEvent) =>
        handleKeyDown(event.nativeEvent),
      onKeyUp: (event: React.KeyboardEvent) => handleKeyUp(event.nativeEvent),
    };
  };
};
